import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { X, User, Lock, Bus, Eye, EyeOff, Phone } from 'lucide-react';
import OtpLoginModal from './OtpLoginModal';

const LoginModal = ({ isOpen, onClose }) => {
  const { login, ROLES } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    role: ROLES.PASSENGER,
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOtpModal, setShowOtpModal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // If Passenger is selected, show OTP modal instead
    if (formData.role === ROLES.PASSENGER) {
      setShowOtpModal(true);
      return;
    }

    // For Manager and Conductor, use password login
    if (!formData.username || !formData.password) {
      setError('Username and password are required');
      return;
    }

    setLoading(true);

    try {
      const result = await login(formData);
      if (result.success) {
        onClose();
        // Redirect conductors to seat status page
        if (formData.role === ROLES.CONDUCTOR) {
          navigate('/seat-status');
        }
        // Reset form
        setFormData({
          role: ROLES.PASSENGER,
          username: '',
          password: ''
        });
      } else {
        // Show user-friendly error message
        const errorMsg = result.error || 'Invalid username or password. Please check your credentials.';
        setError(errorMsg);
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Unable to connect to server. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSuccess = (userData) => {
    setShowOtpModal(false);
    onClose();
    // Reset form
    setFormData({
      role: ROLES.PASSENGER,
      username: '',
      password: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-6">
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-bold text-white">Login to Tripimate</h3>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-blue-100 mt-2">Choose your role and sign in</p>
          </div>

          {/* Body */}
          <div className="px-6 py-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Role Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Role
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, role: ROLES.MANAGER }))}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      formData.role === ROLES.MANAGER
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <User className="w-6 h-6 mx-auto mb-2" />
                    <span className="text-sm font-medium">Manager</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, role: ROLES.PASSENGER }))}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      formData.role === ROLES.PASSENGER
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <User className="w-6 h-6 mx-auto mb-2" />
                    <span className="text-sm font-medium">Passenger</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, role: ROLES.CONDUCTOR }))}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      formData.role === ROLES.CONDUCTOR
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Bus className="w-6 h-6 mx-auto mb-2" />
                    <span className="text-sm font-medium">Conductor</span>
                  </button>
                </div>
              </div>

              {/* Show different forms based on role */}
              {formData.role === ROLES.PASSENGER ? (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center space-x-3">
                    <Phone className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">OTP Login Required</p>
                      <p className="text-xs text-blue-700 mt-1">Passengers must use OTP login with mobile number</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowOtpModal(true)}
                    className="w-full mt-3 bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    Continue with OTP Login
                  </button>
                </div>
              ) : (
                <>
                  {/* Username */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Username
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter username"
                        required
                        maxLength={formData.role === ROLES.CONDUCTOR ? 4 : undefined}
                        pattern={formData.role === ROLES.CONDUCTOR ? '[0-9]{4}' : undefined}
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </>
              )}


              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              {/* Submit Button - Only show for Manager and Conductor */}
              {formData.role !== ROLES.PASSENGER && (
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-xl font-semibold hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Signing in...</span>
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      <span>Sign In</span>
                    </>
                  )}
                </button>
              )}
            </form>

            {/* Info for Manager and Conductor */}
            {formData.role !== ROLES.PASSENGER && (
              <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Login Information:</h4>
                <div className="text-xs text-gray-600 space-y-1">
                  <p><strong>{formData.role === ROLES.MANAGER ? 'Manager' : 'Conductor'}:</strong> Use your username and password to login</p>
                  <p className="text-gray-500 mt-2">Contact administrator if you forgot your password</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* OTP Login Modal for Passengers */}
      <OtpLoginModal 
        isOpen={showOtpModal} 
        onClose={() => setShowOtpModal(false)} 
        onSuccess={handleOtpSuccess}
      />
    </div>
  );
};

export default LoginModal;
